import { createPortal } from "react-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { AvatarImage } from "../../components/AvatarImage";

function initialOf(name: string): string {
  return (name || "?").trim().charAt(0).toUpperCase();
}

// A full name reads as more intimate than a raw email at a glance - first
// name only (matching how the couple would actually refer to each other),
// falling back to the full name if there's no space to split on, and to
// the email only when no display name exists at all. Exported so other
// identity-showing surfaces (ChatListScreen.tsx) share this exact
// fallback logic instead of each re-deriving their own.
export function firstNameOf(fullName: string, email: string): string {
  const trimmed = fullName.trim();
  if (trimmed) return trimmed.split(/\s+/)[0];
  return email;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return "Good night";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function PersonAvatar({ name, avatarFilename, online }: { name: string; avatarFilename: string | null; online?: boolean }) {
  const label = initialOf(name);
  return (
    <div className="couple-header-avatar" title={name}>
      {avatarFilename ? (
        <AvatarImage
          filename={avatarFilename}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
          fallback={label}
        />
      ) : (
        label
      )}
      {online !== undefined && <span className={`couple-header-status-dot${online ? " is-online" : ""}`} aria-hidden="true" />}
    </div>
  );
}

// Portals into #coupleHeaderAnchor - a small emotional identity ("Good
// evening" + first names) sitting above the activity carousel, so Home
// reads as entering a shared private space rather than opening a media
// utility. Still deliberately minimal: no new buttons/menus here, this
// replaces header clutter, it doesn't relocate it. No "Together for X
// days" line - no relationship start-date field exists anywhere in this
// app, and inventing one wasn't part of the brief.
export function CoupleHeader() {
  const presenceUsers = useAppState((s) => s.presenceUsers);
  // Shared identity state (state.ts), seeded once in AuthGate.tsx from the
  // same /api/me call every other startup path already makes, and updated
  // live by ProfileScreen.tsx's name/avatar handlers - this component no
  // longer keeps its own independent copy, so a profile change shows up
  // here without needing its own fetch or a page reload.
  const myEmail = useAppState((s) => s.currentUserEmail);
  const myFullName = useAppState((s) => s.currentUserFullName);
  const myAvatarFilename = useAppState((s) => s.currentUserAvatarFilename);

  const anchor = getVanillaEl<HTMLDivElement>("coupleHeaderAnchor");
  if (!anchor) return null;

  // presenceUsers is every other directory user, unconditionally (see
  // presenceApi.ts) - the partner stays here with their name/avatar intact
  // when offline, only the dot reacts to `online`. In this 2-person app
  // there's normally exactly one entry; if the directory hasn't loaded yet
  // or genuinely has no one else, the header just shows "you" alone rather
  // than inventing a placeholder partner.
  const partner = presenceUsers[0] ?? null;
  const myDisplayName = firstNameOf(myFullName, myEmail);
  const partnerDisplayName = partner ? firstNameOf(partner.fullName, partner.email) : "";

  return createPortal(
    <div className="couple-hero">
      {/* Purely decorative backdrop - low-opacity hearts + a radial glow so
          the identity sits over a subtle romantic surface instead of bare
          page background, richer in dark mode (styles.css). Never a
          bounding card of its own - it blends into the page. */}
      <i className="bi bi-heart-fill couple-hero-deco couple-hero-deco-1" aria-hidden="true" />
      <i className="bi bi-heart-fill couple-hero-deco couple-hero-deco-2" aria-hidden="true" />
      <i className="bi bi-heart-fill couple-hero-deco couple-hero-deco-3" aria-hidden="true" />
      <div className="couple-header">
        <div className="couple-header-eyebrow">{greeting()}</div>
        <div className="couple-header-avatars">
          <PersonAvatar name={myDisplayName} avatarFilename={myAvatarFilename} />
          {partner && (
            <>
              <i className="bi bi-heart-fill couple-header-heart" aria-hidden="true" />
              <PersonAvatar name={partnerDisplayName} avatarFilename={partner.avatarFilename} online={partner.online} />
            </>
          )}
        </div>
        <div className="couple-header-name-line">
          {myDisplayName || "You"}
          {partner && <> &amp; {partnerDisplayName}</>}
        </div>
        {partner && (
          <div className="couple-header-status-line">
            <span className={`couple-header-status-chip${partner.online ? " is-online" : ""}`} aria-hidden="true" />
            {partner.online ? "Online now" : "Offline"}
          </div>
        )}
      </div>
    </div>,
    anchor,
  );
}
